import type { UseMutateFunction } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseRowHeaderWidthOptions<TData = unknown, TVariables = unknown, TContext = unknown> {
  layoutWidth: number | undefined;
  defaultWidth: number;
  mutate: UseMutateFunction<TData, Error, TVariables, TContext>;
}

export function useRowHeaderWidth<TData = unknown, TVariables = unknown, TContext = unknown>({
  layoutWidth,
  defaultWidth,
  mutate,
}: UseRowHeaderWidthOptions<TData, TVariables, TContext>) {
  const [width, setWidth] = useState(layoutWidth ?? defaultWidth);
  const previousWidthRef = useRef(width);

  useEffect(() => {
    if (layoutWidth !== undefined) {
      setWidth(layoutWidth);
      previousWidthRef.current = layoutWidth;
    }
  }, [layoutWidth]);

  const setRowHeaderWidth = useCallback(
    (newWidth: number) => {
      const oldWidth = previousWidthRef.current;
      previousWidthRef.current = newWidth;
      setWidth(newWidth);

      mutate({ rowHeaderWidth: newWidth } as TVariables, {
        onError: () => {
          setWidth(oldWidth);
          previousWidthRef.current = oldWidth;
        },
      });
    },
    [mutate],
  );

  return { rowHeaderWidth: width, setRowHeaderWidth };
}
